import { useState, useEffect } from "react";
import { Menu, X, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link, useLocation } from "react-router-dom";
import ContactForm from "@/components/ContactForm";
import logoRoiDuStreaming from "@/assets/logo-roi-du-streaming-iptv.webp";

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  const navItems = [
    { name: "Accueil", href: "/" },
    { name: "Nos Offres", href: "/nos-offres" },
    { name: "Tutoriels", href: "/tutoriels" },
    { name: "Avis Clients", href: "/avis-clients" },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const scrollToSection = (href: string) => {
    if (href.startsWith("#")) {
      const element = document.querySelector(href);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    }
    setIsMobileMenuOpen(false);
  };

  const isActive = (href: string) => {
    if (href === "/") return location.pathname === "/";
    return location.pathname.startsWith(href);
  };

  return (
    <>
      {/* Main Header */}
      <header 
        className={`sticky top-0 z-50 transition-all duration-300 ${
          isScrolled 
            ? "bg-background/98 backdrop-blur-md shadow-lg border-b border-border/50" 
            : "bg-background/95 backdrop-blur-sm border-b border-border/30"
        }`}
      >
        <div className="container mx-auto px-3 md:px-4">
          <div className="flex items-center justify-between h-14 md:h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2 md:gap-3 min-h-[48px] min-w-[48px]">
              <img 
                src={logoRoiDuStreaming} 
                alt="Roi du Streaming" 
                width={112}
                height={112}
                className="h-10 md:h-12 w-auto"
              />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-1 xl:gap-2">
              {navItems.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`text-sm xl:text-base font-medium px-3 xl:px-4 py-2 rounded-lg transition-all duration-200 ${
                    isActive(item.href)
                      ? "text-primary bg-primary/10"
                      : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                  }`}
                >
                  {item.name}
                </Link>
              ))}
              <ContactForm
                trigger={
                  <button className="text-sm xl:text-base font-medium px-3 xl:px-4 py-2 text-muted-foreground hover:text-foreground hover:bg-muted/50 rounded-lg transition-all duration-200">
                    Contact
                  </button>
                }
              />
            </nav>

            {/* CTA Button - Desktop */}
            <div className="hidden lg:block">
              <Button
                onClick={() => scrollToSection("#pricing")}
                className="btn-primary text-white font-semibold px-4 xl:px-6 text-sm xl:text-base"
                aria-label="Voir les offres d'abonnement IPTV"
              >
                Je m'abonne !
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="lg:hidden text-foreground p-3 min-h-[48px] min-w-[48px] flex items-center justify-center hover:bg-muted/50 rounded-lg transition-colors"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label={isMobileMenuOpen ? "Fermer le menu" : "Ouvrir le menu"}
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <div
          className={`lg:hidden overflow-hidden transition-all duration-300 ease-in-out ${
            isMobileMenuOpen ? "max-h-[400px] opacity-100" : "max-h-0 opacity-0"
          }`}
        >
          <div className="bg-background border-t border-border/30">
            <div className="container mx-auto px-4 py-4 space-y-1">
              {navItems.map((item, index) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`flex items-center justify-between py-3 px-4 rounded-lg transition-all duration-200 ${
                    isActive(item.href)
                      ? "text-primary bg-primary/10 font-medium"
                      : "text-foreground hover:bg-muted/50"
                  }`}
                  style={{ animationDelay: `${index * 50}ms` }}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <span>{item.name}</span>
                  <ChevronRight size={18} className="text-muted-foreground" />
                </Link>
              ))}
              <ContactForm
                trigger={
                  <button className="w-full flex items-center justify-between py-4 px-4 min-h-[48px] text-foreground hover:bg-muted/50 rounded-lg transition-all duration-200">
                    <span>Contact</span>
                    <ChevronRight size={18} className="text-muted-foreground" />
                  </button>
                }
              />
              <div className="pt-3">
                <Button
                  onClick={() => scrollToSection("#pricing")}
                  className="w-full btn-primary text-white font-semibold py-4 min-h-[48px]"
                >
                  Je m'abonne !
                </Button>
              </div>
            </div>
          </div>
        </div>
      </header>
    </>
  );
};

export default Header;